import { useState } from "react";
import { api, LinkCode } from "../api";
import { Button } from "./ui/Button";
import { Copy, ExternalLink, Check } from "lucide-react";
import { greetingFor } from "../lib/greetings";

interface Props {
  disabled?: boolean;
  disabledReason?: string;
  /** The user's target language code — drives the expected-reply greeting hint. */
  langCode?: string | null;
}

export default function LinkCard({ disabled, disabledReason, langCode }: Props) {
  const [link, setLink] = useState<LinkCode | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function onCreate() {
    setErr(null);
    setCopied(false);
    setBusy(true);
    try {
      const res = await api.createLinkCode();
      setLink(res);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Could not create link");
    } finally {
      setBusy(false);
    }
  }

  async function onCopy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.deep_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setErr("Copy failed — select and copy manually.");
    }
  }

  return (
    <div>
      <p className="text-ink-soft text-[0.95rem] leading-relaxed">
        Tap the link below on the same device where Telegram is installed. Telegram opens and the tutor replies with{" "}
        <span className="italic text-terracotta font-medium">{greetingFor(langCode)}!</span> — that's your confirmation.
      </p>
      {!link && (
        <div className="mt-5">
          <Button onClick={onCreate} disabled={busy || disabled}>
            {busy ? "Preparing…" : "Open Telegram"} <ExternalLink size={14} />
          </Button>
        </div>
      )}
      {disabled && disabledReason && <p className="mt-3 text-xs text-ink-faint">{disabledReason}</p>}
      {link && (
        <div className="mt-5 space-y-4">
          <a
            href={link.deep_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded bg-terracotta text-paper font-medium hover:bg-terracotta-soft transition-colors"
          >
            Open Telegram <ExternalLink size={14} />
          </a>
          <div>
            <p className="text-xs text-ink-faint mb-2">
              Or paste this code in Telegram after tapping <code className="font-mono">/start</code>:
            </p>
            <div className="flex items-center gap-2">
              <code className="font-mono text-sm px-3 py-2 bg-ink-card text-paper rounded">{link.code}</code>
              <Button variant="outline" size="sm" onClick={onCopy} type="button">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </Button>
            </div>
          </div>
          <p className="text-xs text-ink-faint">Code expires in 15 minutes. Generate a new one if it runs out.</p>
        </div>
      )}
      {err && <p className="mt-3 text-sm text-terracotta font-medium">{err}</p>}
    </div>
  );
}
